import type {LotoPlan} from '@/types/Loto'
import AssetPositionDialog from '@/components/loto/AssetPositionDialog.vue'
import AssetPositionNotice from '@/components/loto/AssetPositionNotice.vue'
import {useLotoAreaStore} from '@/api/loto'
import {t, useGlobalSetting, useSecondPopup} from '@/lib/utils'

export interface GridLineNum { n: number; x: number }
export interface GridLineLetter { l: string; y: number }
export interface GridConfig { rows: GridLineNum[]; cols: GridLineLetter[] }
export interface ParsedPosition { rowNum: number; colLetter: string; levelMeters: number | null }

type PositionValue = string | number | null | undefined

/**
 * Positionsfelder kommen direkt aus Supabase in snake_case, bestehende App-Objekte
 * und die urspruengliche Motorenliste verwenden dagegen camelCase.
 */
export interface AssetPositionSource {
    asset_id?: PositionValue
    assetId?: PositionValue
    location_row?: PositionValue
    locationRow?: PositionValue
    location_col?: PositionValue
    locationCol?: PositionValue
    location_level?: PositionValue
    locationLevel?: PositionValue
}

interface AssetPositionValues {
    assetId: string
    row: PositionValue
    col: PositionValue
    level: PositionValue
}

function firstPositionValue(...values: PositionValue[]): PositionValue {
    return values.find(value => value != null && String(value).trim() !== '' && String(value).trim() !== '-1')
}

export function getAssetPositionValues(drive: AssetPositionSource | null | undefined): AssetPositionValues | null {
    if (!drive) return null
    return {
        assetId: String(firstPositionValue(drive.asset_id, drive.assetId) ?? '').trim(),
        row: firstPositionValue(drive.location_row, drive.locationRow),
        col: firstPositionValue(drive.location_col, drive.locationCol),
        level: firstPositionValue(drive.location_level, drive.locationLevel),
    }
}

export function parseLevel(text: PositionValue): number | null {
    if (text == null) return null
    const normalized = String(text).trim().toLowerCase().replace(/\s*m\s*$/, '').replace(',', '.')
    if (!normalized || normalized === '-1' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null
    const value = Number(normalized)
    return Number.isFinite(value) && value >= 0 ? value : null
}

export function parsePosition(row: PositionValue, col: PositionValue, level: PositionValue): ParsedPosition | null {
    const rowText = row == null ? '' : String(row).trim()
    const colLetter = col == null ? '' : String(col).trim().toUpperCase()
    if (!/^\d+$/.test(rowText) || !/^[A-Z]$/.test(colLetter)) return null
    const rowNum = Number(rowText)
    const levelMeters = parseLevel(level)
    if (!Number.isInteger(rowNum) || rowNum < 1 || rowNum > 99 || levelMeters == null) return null
    return {rowNum, colLetter, levelMeters}
}

function interpolateAxis<T>(lines: T[], target: number, key: (line: T) => number, value: (line: T) => number): number | null {
    const valid = lines.filter(line => Number.isFinite(key(line)) && Number.isFinite(value(line))).sort((a, b) => key(a) - key(b))
    if (!valid.length) return null
    const exact = valid.find(line => key(line) === target)
    if (exact) return value(exact)
    if (target < key(valid[0]) || target > key(valid[valid.length - 1])) return null
    for (let i = 0; i < valid.length - 1; i++) {
        const before = valid[i], after = valid[i + 1]
        const from = key(before), to = key(after)
        if (from < target && target < to && to !== from) {
            const ratio = (target - from) / (to - from)
            return value(before) + ratio * (value(after) - value(before))
        }
    }
    return null
}

export function gridToRelative(config: GridConfig, pos: ParsedPosition): { x: number; y: number } | null {
    if (!config || !Array.isArray(config.rows) || !Array.isArray(config.cols)) return null
    const x = interpolateAxis(config.rows, pos.rowNum, line => line.n, line => line.x)
    const colIndex = pos.colLetter.charCodeAt(0) - 65
    const y = interpolateAxis(config.cols, colIndex, line => String(line.l).toUpperCase().charCodeAt(0) - 65, line => line.y)
    return x == null || y == null ? null : {x, y}
}

export function resolvePlanPosition(plans: LotoPlan[], pos: ParsedPosition): { plan: LotoPlan; point: { x: number; y: number } } | null {
    const candidates = plans.filter(plan =>
        plan.grid_config && plan.level_height != null && String(plan.level_height).trim() !== '' && Number.isFinite(Number(plan.level_height)))
    if (!candidates.length || pos.levelMeters == null) return null
    const heights = [...new Set(candidates.map(plan => Number(plan.level_height)))].sort((a, b) => a - b)
    const lower = heights.filter(height => height <= pos.levelMeters!)
    const height = lower.length ? lower[lower.length - 1] : heights[0]
    const levelPlans = candidates.filter(plan => Number(plan.level_height) === height)
        .sort((a, b) => a.sort_order - b.sort_order || a.group_name.localeCompare(b.group_name, 'de') || a.id - b.id)
    for (const plan of levelPlans) {
        const point = gridToRelative(plan.grid_config!, pos)
        if (point && point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1) return {plan, point}
    }
    return null
}

export function canShowPosition(drive: AssetPositionSource | null | undefined): boolean {
    const position = getAssetPositionValues(drive)
    return !!position && parsePosition(position.row, position.col, position.level) !== null
}

function showNoCalibration(): void {
    useSecondPopup().open(AssetPositionNotice, {message: t('assetPosition.noCalibration')}, {
        dialogTitle: t('assetPosition.mapTitle'), maxWidth: 520, fullscreen: false, persistent: false,
    })
}

export async function markAssetAt(row: string | number, col: string | number, level: string | number, label = ''): Promise<void> {
    const pos = parsePosition(row, col, level)
    if (!pos) return showNoCalibration()
    const settings = useGlobalSetting()
    settings.loadingMessage = t('loading.assetPosition')
    settings.isLoading = true
    try {
        const store = useLotoAreaStore()
        await store.load()
        const resolved = resolvePlanPosition(store.plans, pos)
        if (!resolved?.plan.image_path) return showNoCalibration()
        const rawLevel = String(level).trim().replace(/\s*m\s*$/i, '').trim()
        useSecondPopup().open(AssetPositionDialog, {
            planId: resolved.plan.id,
            marker: resolved.point,
            label,
            positionText: `${pos.rowNum}${pos.colLetter} \u00b7 ${rawLevel} m`,
        }, {contentFilled: true, maxWidth: 1400, fullscreen: settings.isMobile, persistent: false})
    } catch (error) {
        console.error('Asset-Position konnte nicht geoeffnet werden:', error)
        showNoCalibration()
    } finally {
        settings.isLoading = false
    }
}

export function openAssetPositionMap(drive: AssetPositionSource): Promise<void> {
    const position = getAssetPositionValues(drive)
    if (!position) return Promise.resolve(showNoCalibration())
    return markAssetAt(position.row ?? '', position.col ?? '', position.level ?? '', position.assetId)
}
